import React, { FC, useRef } from 'react';

import {
  MonoFontLabel,
  OpenSansParagraph,
  RecoletaHeading,
} from '../common/typography';
import { markdownText } from '../../pages/doctors';
import Slider, { Settings } from 'react-slick';
import SliderArrow from '../common/slider/SliderArrow';
import theme from '../../styles/theme';
import Container from '../Homepage/Container';
import { useIntl } from 'react-intl';
import Knowledge from '../../assets/svgs/product/img-module-knowledge.svg';
import Emotions from '../../assets/svgs/product/img-module-emotions.svg';
import Nutrition from '../../assets/svgs/product/img-module-nutrition.svg';
import Hypnosis from '../../assets/svgs/product/img-module-hypnosis.svg';

export interface PersonalizedContentProps {
  prefix: string;
  title: string;
  description: string;
  color: string;
  src: string;
  isFullWidth?: boolean;
}

const settings: Settings = {
  dots: false,
  infinite: true,
  autoplay: false,
  centerPadding: '0px',
  speed: 500,
  slidesToShow: 2,
  slidesToScroll: 2,
  nextArrow: <SliderArrow position="right" />,
  prevArrow: <SliderArrow position="left" />,
  responsive: [
    {
      breakpoint: theme.breakpoints.lg,
      settings: {
        arrows: false,
        slidesToShow: 1,
        slidesToScroll: 1,
        draggable: true,
      },
    },
  ],
};

const Card: FC<PersonalizedContentProps> = ({
  prefix,
  title,
  description,
  color,
  src,
  isFullWidth,
}) => {
  const isMobile: boolean =
    typeof window !== 'undefined' &&
    window.matchMedia('@media (max-width: 768px)').matches;

  const determineWidth = () => {
    if (isMobile) {
      return false;
    }

    return isFullWidth;
  };

  return (
    <div
      css={{
        minHeight: determineWidth() ? 200 : 400,
        height: 'auto',
        backgroundColor: color,
        padding: '1rem',
        borderRadius: '1rem',
        margin: '.5rem',
        gridColumn: determineWidth() ? '1 / 3' : 'auto',
      }}
    >
      {determineWidth() ? (
        <div
          css={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div
            css={{
              flex: 2,
            }}
          >
            <MonoFontLabel
              css={{
                fontWeight: 700,
                letterSpacing: '2px',
                lineHeight: '18px',
                color: '#00000080',
                marginBottom: 0,
              }}
            >
              {prefix}
            </MonoFontLabel>
            <RecoletaHeading
              css={{
                fontSize: '24px',
                fontWeight: 600,
                maxWidth: 400,
              }}
            >
              {title}
            </RecoletaHeading>
          </div>

          <img
            src={src}
            alt={title}
            css={{
              flex: 1,
              height: 80,
              width: 80,
              marginLeft: '2rem',
            }}
          />

          <OpenSansParagraph
            css={{
              flex: 3,
              fontSize: '16px',
              fontWeight: 400,
              lineHeight: '25px',
              marginTop: '1rem',
              width: '100%',
              marginLeft: '1rem',
            }}
          >
            {markdownText(description)}
          </OpenSansParagraph>
        </div>
      ) : (
        <>
          <div
            css={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div>
              <MonoFontLabel
                css={{
                  fontWeight: 700,
                  letterSpacing: '2px',
                  lineHeight: '18px',
                  color: '#00000080',
                  marginBottom: 0,
                }}
              >
                {prefix}
              </MonoFontLabel>
              <RecoletaHeading
                css={{
                  fontSize: '24px',
                  fontWeight: 600,
                  maxWidth: 400,
                }}
              >
                {title}
              </RecoletaHeading>
            </div>
            <img src={src} alt={title} />
          </div>

          <OpenSansParagraph
            css={{
              fontSize: '16px',
              fontWeight: 400,
              lineHeight: '25px',
              marginTop: '1rem',
              width: '100%',
            }}
          >
            {markdownText(description)}
          </OpenSansParagraph>
        </>
      )}
    </div>
  );
};

interface Props {
  dataObj: PersonalizedContentProps[];
}

const PersonalizedContent: FC<Props> = ({ dataObj: contentItems }) => {
  const ref = useRef<Slider>(null);
  const isMobile: boolean =
    typeof window !== 'undefined' &&
    window.matchMedia('@media (max-width: 768px)').matches;
  const intl = useIntl();

  const contentData = contentItems.length > 0 ? contentItems : [
    {
      prefix: intl.formatMessage({
        id: 'product.page.personalised.content.item1.prefix',
        defaultMessage: 'Pflichtmodul:',
      }),
      title: intl.formatMessage({
        id: 'product.page.personalised.content.item1.title',
        defaultMessage: 'Basiswissen',
      }),
      description: intl.formatMessage({
        id: 'product.page.personalised.content.item1.text',
        defaultMessage:
          'Werde zur Expertin/zum Experten deiner Erkrankung und lege so die Basis für eine erfolgreiche App-Therapie.\n\nDieses Modul vermittelt dir u.a. Wissen zu Ursachen, Diagnostik, Behandlungsstrategien und Lebensstilfaktoren.',
      }),
      color: '#DDF2F8',
      src: Knowledge,
    },
    {
      prefix: intl.formatMessage({
        id: 'product.page.personalised.content.item2.prefix',
        defaultMessage: 'optionales Modul:',
      }),
      title: intl.formatMessage({
        id: 'product.page.personalised.content.item2.title',
        defaultMessage: 'Gefühle & Verhalten',
      }),
      description: intl.formatMessage({
        id: 'product.page.personalised.content.item2.text',
        defaultMessage:
          'Entwickle Strategien, um den negativen Einfluss hinderlicher Gedanken, Gefühle und Verhaltensweisen auf deine Psyche und dein körperliches Wohlbefinden zu reduzieren.\n\nIm Rahmen des Moduls werden dir zusätzlich ein Gedanken- und Schlaftagebuch sowie angeleitete Entspannungsübungen zur Verfügung gestellt.',
      }),
      color: '#FBE5EF',
      src: Emotions,
    },
    {
      prefix: intl.formatMessage({
        id: 'product.page.personalised.content.item3.prefix',
        defaultMessage: 'optionales Modul:',
      }),
      title: intl.formatMessage({
        id: 'product.page.personalised.content.item3.title',
        defaultMessage: 'Ernährung',
      }),
      description: intl.formatMessage({
        id: 'product.page.personalised.content.item3.text',
        defaultMessage:
          'Identifiziere im Rahmen der Low-FODMAP-Diät deine persönlichen Symptomauslöser und finde Schritt für Schritt eine verträgliche Dauerernährung.\n\nZusätzlich erhältst du personalisierte Empfehlungen zum Umgang mit bestehenden Allergien und Intoleranzen sowie eine auf dich angepasste Lebensmittelliste.',
      }),
      color: '#FFF8CC',
      src: Nutrition,
    },
    {
      prefix: intl.formatMessage({
        id: 'product.page.personalised.content.item4.prefix',
        defaultMessage: 'optionales Modul:',
      }),
      title: intl.formatMessage({
        id: 'product.page.personalised.content.item4.title',
        defaultMessage: 'Audio-geführte Hypnose',
      }),
      description: intl.formatMessage({
        id: 'product.page.personalised.content.item4.text',
        defaultMessage:
          'Ziel der darmgerichteten Hypnose ist es die Darm-Hirn-Achse durch Suggestionen positiv zu beeinflussen und so deine Beschwerden durch Entspannung zu lindern.\n\nLerne die Grundlagen der Hypnose kennen, werde durch Übungen unterstützt und führe regelmäßig audio-geführte Hypnosen durch.',
      }),
      color: '#E9DDEC',
      src: Hypnosis,
    },
  ];

  const renderMobileView = () => {
    return (
      <Slider
        ref={ref}
        {...settings}
        css={{
          width: '100%',
          marginTop: '2rem',
        }}
      >
        {contentData.map((item, i) => (
          <Card key={i} {...item} />
        ))}
      </Slider>
    );
  };

  const renderLargeScreenView = () => {
    return (
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gridGap: '1rem',
          marginTop: '2rem',
        }}
      >
        {contentData.map((item, i) => (
          <Card
            key={i}
            {...item}
            isFullWidth={i === 0 && contentData.length % 2 === 1}
          />
        ))}
      </div>
    );
  };

  return (
    <Container
      css={{
        maxWidth: 900,
        marginTop: '2rem',
        marginBottom: isMobile ? '2rem' : '2rem',
        paddingLeft: 0,
        paddingRight: 0,
      }}
    >
      {/* Mobile view with Slider */}
      <div
        css={{
          maxWidth: 900,
          marginTop: '2rem',
          paddingLeft: 0,
          paddingRight: 0,
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
        }}
      >
        <RecoletaHeading
          css={{
            fontSize: '32px',
            fontWeight: 500,
            lineHeight: '38px',
            textAlign: 'center',
          }}
        >
          {markdownText(
            intl.formatMessage({
              id: "product.page.personalised.content.title",
              defaultMessage:
                "<b style='color: #2AAB3C'>Dein Programm</b><br/>",
            })
          )}
        </RecoletaHeading>
        <OpenSansParagraph
          css={{
            textAlign: 'center',
            maxWidth: 600,
          }}
        >
          {markdownText(
            intl.formatMessage({
              id: "product.page.personalised.content.text",
              defaultMessage:
                "Im ersten Fragebogen erfassen wir deine persönlichen Bedürfnisse und Präferenzen. Dazu zählen medizinische Faktoren wie beispielsweise Kontraindikationen und Angaben zu deinem Lebensstil. Anhand dieser Angaben entscheidet unser Algorithmus, welche der folgenden Module für dich geeignet und sicher sind:",
            })
          )}
        </OpenSansParagraph>
      </div>

      {isMobile ? renderMobileView() : renderLargeScreenView()}
    </Container>
  );
};

export default PersonalizedContent;
